import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import BackstageComment from 'interfaces/BackstageComment.interface';
import { INftState, NftType } from 'interfaces/NftData.interface';
import { getAllNfts } from '../../APIs/HotAuctionApi';

export const initialState: INftState = {
  nfts: undefined,
  loading: false,
  nftIndex: 0,
  preferredCategories: undefined,
};

export const getNfts = createAsyncThunk('nfts/getNfts', async thunkAPI => {
  const nfts = await getAllNfts();
  return nfts.data;
});

export const nftSlice = createSlice({
  name: 'nft',
  initialState,
  reducers: {
    addCommentState: (
      state: INftState,
      action: PayloadAction<{
        backstageComment?: BackstageComment;
        index?: number;
        nftId: number;
        children?: boolean;
      }>
    ) => {
      const { index, backstageComment, nftId, children } = action.payload;
      if (state.nfts) {
        const nftIndex = state.nfts.findIndex(nft => nft.id === nftId);
        if (!backstageComment && (index || index === 0)) {
          state.nfts[nftIndex].comments[index].children = [];
        } else if (children && index && backstageComment) {
          state.nfts[nftIndex].comments[index].children?.push(backstageComment);
        } else if (backstageComment) {
          state.nfts[nftIndex].comments.push(backstageComment);
        }
      }
    },
    addLike: (
      state: INftState,
      action: PayloadAction<{ id: number; currentIndex: number }>
    ) => {
      const { id, currentIndex } = action.payload;
      if (state.nfts && state.nfts[currentIndex].likes) {
        if (!state.nfts[currentIndex].likes.includes(id)) {
          state.nfts[currentIndex].likes = [
            ...state.nfts[currentIndex].likes,
            id,
          ];
        } else {
          state.nfts[currentIndex].likes = state.nfts[
            currentIndex
          ].likes.filter(like => like !== id);
        }
      } else if (state.nfts) {
        state.nfts[currentIndex].likes = [id];
      }
    },
    randomizeNfts: (state: INftState) => {
      if (state.nfts) {
        const newArr = state.nfts.slice();
        for (let i = newArr.length - 1; i > 0; i--) {
          const rand = Math.floor(Math.random() * (i + 1));
          [newArr[i], newArr[rand]] = [newArr[rand], newArr[i]];
        }
        state.nfts = newArr;
      }
    },
    setNftIndex: (state: INftState, action: PayloadAction<number>) => {
      state.nftIndex = action.payload;
    },
    updatePreferredCategories: (
      state: INftState,
      action: PayloadAction<NftType[]>
    ) => {
      state.preferredCategories = [...action.payload];

      if (state.nfts && state.preferredCategories.length > 0) {
        state.nfts = state.nfts.filter(nft =>
          nft.categories.some(category =>
            state.preferredCategories?.some(prefCat => prefCat === category)
          )
        );
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(getNfts.fulfilled, (state, action) => {
      state.nfts = action.payload.filter(nft => nft.image !== null);

      state.loading = false;
    });
    builder.addCase(getNfts.pending, state => {
      state.loading = true;
    });
    builder.addCase(getNfts.rejected, state => {
      state.loading = false;
    });
  },
});

export const selectNft = (state: RootState): INftState => state.nft;
export const {
  addCommentState,
  addLike,
  randomizeNfts,
  setNftIndex,
  updatePreferredCategories,
} = nftSlice.actions;
export default nftSlice.reducer;
